import { useEffect, useState, useMemo, ComponentProps } from 'react';

import { MultiSelectInput } from '../forms';
import {
  universalReferenceActions,
  RefValueItemInterface,
} from '../../../universal-reference/store/universalReferenceStore';

/**
 * Мультивыбор значений универсального справочника (universal-reference).
 * Одиночная версия — RefValueSelector; типы refId/int1/filterKey/excludeKey
 * сознательно совпадают с RefValueSelectorOwnProps.
 */
type MultiValueRefValueSelectorProps = Omit<ComponentProps<typeof MultiSelectInput>, 'value' | 'defaultValue'> & {
  /**
   * Идентификатор справочника (universal-reference), из которого грузятся значения.
   * Строка или число — совместимость с legacy-вызовами (`refId='27'`, `refId={ 52 }`).
   */
  refId: number | string,
  /** Фильтр значений по полю int1 (сравнение через String(...)). */
  int1?: number | string,
  /** Показывать только опции с id из этого набора. */
  filterKey?: unknown[],
  /** Исключить опции с id из этого набора. */
  excludeKey?: unknown[],
};

const MultiValueRefValueSelector = (props: MultiValueRefValueSelectorProps) => {
  const {
    refId,
    int1,
    filterKey,
    excludeKey,
    disabled,
    name,
    ...rest
  } = props;

  const [loading, setLoading] = useState(false);
  // Элементы универсального справочника — полиморфная форма ({ id, string1, int1, sort, ... }),
  // сужать под каждый refId без generic'а нельзя (как в RefValueSelector).
  const [values, setValues] = useState<RefValueItemInterface[]>([]);

  // Мемоизированная фильтрация
  const filteredValues = useMemo(() => {
    let fv = values;
    if (int1) {
      fv = fv.filter(v => String(v.int1) === String(int1));
    }
    if (filterKey) {
      fv = fv.filter(v => filterKey.find(k => v.id === k));
    }
    if (excludeKey) {
      fv = fv.filter(v => !(excludeKey.find(k => v.id === k)));
    }
    return fv;
  }, [excludeKey, filterKey, int1, values]);

  useEffect(() => {
    let isSubscribed = true;

    setLoading(true);
    // экшен стора кэширует значения по refId на сессию — повторные mount'ы идут без запроса
    universalReferenceActions.getRefValues(refId)
      .then((refValues) => isSubscribed && setValues(refValues))
      .finally(() => isSubscribed && setLoading(false));

    return () => { isSubscribed = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  return (
    <MultiSelectInput
      name={ name }
      dataTitle={ props.dataTitle ?? 'name' }
      dataValue={ props.dataValue ?? 'id' }
      data={ filteredValues }
      noOptionsMessage={ () => { return 'Нет вариантов'; } }
      isLoading={ loading }
      isDisabled={ disabled }
      mapValuesToField={ (vals: unknown[]) => vals.map(v => (v as { value: unknown }).value) }
      mapFieldToSelectedOptions={ (options, vals: unknown[]) => (options?.filter(item => vals.includes(item.value))) }
      isMulti
      { ...rest }
    />
  );
};

export default MultiValueRefValueSelector;
